/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Fragment } from "react";
import { Else, If, Then, When } from "react-if";
import { FlexContainer, Text, theme } from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { getIndex, getTextColor, mapAppEnvs, mapValues } from "./helpers";
import { OptionDeleteButton, StyledOption } from "./styles";
import { EnvType, OptionType } from "./types";

type Props = {
  filter: string;
  valueArray: string[];
  data: EnvType[];
  isFilterExcluded: boolean;
  onRemoveValueHandler: (inputName: string, index: number) => void;
};

export function ActiveFilterItemMapped({
  filter,
  valueArray,
  data,
  isFilterExcluded,
  onRemoveValueHandler,
}: Props) {
  const isEnvironmentFilter = filter === "environment";

  const mappedData = isEnvironmentFilter
    ? mapAppEnvs(data)
    : mapValues(valueArray, data);

  return (
    <>
      {mappedData?.map(
        ({ nid, name: itemName, envs }: OptionType, index: number) => {
          const isEnvDefined = isNotNil(envs);
          return (
            <Fragment key={`filter-value-${nid}`}>
              <FlexContainer key={nid} justifyContent="space-between">
                <If condition={isEnvironmentFilter}>
                  <Then>
                    <Text
                      nowrap
                      size="sm"
                      mb="0"
                      mr={theme.spacing.spacing04}
                      weight="medium"
                      color={theme.color.text.text02}
                    >
                      {itemName}
                    </Text>
                  </Then>
                  <Else>
                    <Text
                      nowrap
                      size="sm"
                      mb="0"
                      mr={theme.spacing.spacing04}
                      color={getTextColor(isFilterExcluded)}
                    >
                      {itemName}
                    </Text>
                  </Else>
                </If>
                <When condition={!isEnvDefined}>
                  <OptionDeleteButton
                    icon="trash"
                    size="sm"
                    severity="low"
                    onClick={() =>
                      !isEnvironmentFilter
                        ? onRemoveValueHandler(filter, index)
                        : undefined
                    }
                  />
                </When>
              </FlexContainer>
              <When condition={isEnvironmentFilter && isEnvDefined}>
                {envs?.map(({ nid: envNid, name: envName }: EnvType) => (
                  <StyledOption key={envNid} justifyContent="space-between">
                    <Text
                      nowrap
                      size="sm"
                      mb="0"
                      mr={theme.spacing.spacing04}
                      color={getTextColor(isFilterExcluded)}
                    >
                      {envName}
                    </Text>
                    <OptionDeleteButton
                      icon="trash"
                      size="sm"
                      severity="low"
                      onClick={() =>
                        onRemoveValueHandler(filter, getIndex(envNid, data))
                      }
                    />
                  </StyledOption>
                ))}
              </When>
            </Fragment>
          );
        }
      )}
    </>
  );
}
